import React, { useState, useEffect, useRef, useCallback } from 'react';
import askConfirm from './Confirm';
import Icon from './Icon';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Markdown from './Markdown';

const TYPES = [
  { key: 'phone', label: '📞 Phone call' },
  { key: 'in_person', label: '🤝 In person' },
  { key: 'online', label: '💻 Online' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'note', label: 'Note' },
];
const typeLabel = (t) => (TYPES.find(x => x.key === t) || { label: t }).label;
const fmtDur = (s) => { if (!s) return ''; const m = Math.floor(s / 60), sec = s % 60; return `${m}:${String(sec).padStart(2, '0')}`; };
const fmtWhen = (s) => s ? new Date(s).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

// Audio goes up as bytes on its own request, and the save that follows names it by id. Sending it
// as base64 inside the JSON body is what broke a 71-minute recording: a third larger than the
// file, over the body limit, and refused by the parser before any of our code could say so.
//
// Two requests, and only one of them is large. The failure of each can also be told apart, which
// matters when the thing that failed is an hour of somebody's meeting.
async function uploadAudio(clientId, blob, mime, durationSec) {
  const r = await api.post(`/clients/${clientId}/conversations/audio`, blob, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Audio-Type': mime || 'audio/webm',
      ...(durationSec ? { 'X-Audio-Seconds': String(Math.round(durationSec)) } : {}),
    },
    // A long recording over a slow connection is not a stuck request.
    timeout: 20 * 60 * 1000,
  });
  return r.data.audio_id;
}


export default function ClientConversations({ clientId, clientName }) {
  const [status, setStatus] = useState(null);      // { enabled, provider, ai }
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);          // new-conversation panel

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get('/transcribe/status').catch(() => ({ data: { enabled: false, ai: false } })),
        api.get(`/clients/${clientId}/conversations`),
      ]);
      setStatus(s.data); setList(r.data); setErr('');
    } catch (e) { setErr(e.response?.data?.error || 'Could not load conversations.'); setList([]); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useWhileWorking(list, setList);

  const submit = async ({ type, blob, mime_type, duration_sec, transcript }) => {
    setBusy(true); setErr('');
    try {
      const audio_id = blob ? await uploadAudio(clientId, blob, mime_type, duration_sec) : null;
      const r = await api.post(`/clients/${clientId}/conversations`, { type, audio_id, mime_type, duration_sec, transcript },
        // Transcribing an hour takes minutes. The default would give up long before it finished.
        { timeout: 30 * 60 * 1000 });
      setList(l => [r.data, ...(l || [])]);
      // Closed straight away. The card carries on by itself, which is the point — a recording is
      // not something to stand and watch.
      setOpen(false);
    }
    catch (e) {
      // A size limit that reports itself as "could not save" is a limit nobody can work around, and
      // that is exactly what happened to a 71-minute recording: the body parser refused it with a
      // response that was not JSON, so there was nothing here to read.
      const d = e.response && e.response.data;
      const status = e.response && e.response.status;
      setErr(
        (d && d.error)
        || (status === 413 ? 'That recording is too large to upload. Record at a lower quality, or split a long call into parts.' : '')
        || (e.code === 'ECONNABORTED' ? 'That took too long and the connection gave up. A very long recording is better split into parts.' : '')
        || (status ? `The server refused it (${status}).` : '')
        || (e.message ? `Could not save that conversation: ${e.message}` : 'Could not save that conversation.'));
    }
    setBusy(false);
  };

  const removeConv = async (id) => {
    if (!await askConfirm({ body: 'Delete this conversation and its notes?', label: 'Delete it' })) return;
    try { await api.delete(`/conversations/${id}`); setList(l => l.filter(c => c.id !== id)); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not delete.'); }
  };

  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0 }}>Conversations</h3>
          <div className="hint">Record or upload a call or meeting — AI writes the minutes and suggests follow-up tasks.</div>
          <div className="hint hint-xs" style={{ marginTop: 2 }}><Icon name="lock" /> Private to you (the practice owner) — staff can’t see recordings or transcripts, and they’re kept out of staff AI answers.</div>
        </div>
        {!open && <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>+ New conversation</button>}
      </div>

      {status && !status.enabled && !status.ai && (
        <div className="conv-hint">Transcription and AI aren’t set up on the server yet — you can still paste a transcript to keep a record. Add a Deepgram/OpenAI key (transcription) and an Anthropic key (minutes) to enable recording.</div>
      )}
      {status && !status.enabled && status.ai && (
        <div className="conv-hint">Audio transcription isn’t set up (add a Deepgram or OpenAI key). For now, paste a transcript below and AI will write the minutes.</div>
      )}
      {err && <div className="conv-err">{err}</div>}

      {open && <NewConversation status={status} busy={busy} onCancel={() => setOpen(false)} onSubmit={submit} />}

      {list == null ? <div className="hint hint-base" style={{ marginTop: 12 }}>Loading…</div>
        : list.length === 0 && !open ? <div className="hint hint-base" style={{ marginTop: 12 }}>No conversations yet.</div>
          : <div className="conv-list">{list.map(c => <ConversationCard key={c.id} conv={c} clientName={clientName} onDelete={() => removeConv(c.id)} onChange={(u) => setList(l => l.map(x => x.id === u.id ? u : x))} />)}</div>}
    </div>
  );
}

// Anything still being worked on is asked after every so often until it is done. Polling rather
// than anything cleverer: there is at most one of these at a time, it is over in a few minutes,
// and a socket for it would be more moving parts than the problem has.
function useWhileWorking(list, setList) {
  const working = (list || []).filter(c => c.status === 'transcribing').map(c => c.id).join(',');
  useEffect(() => {
    if (!working) return undefined;
    const ids = working.split(',');
    const timer = setInterval(async () => {
      for (const id of ids) {
        try {
          const r = await api.get(`/conversations/${id}`);
          if (r.data && r.data.status !== 'transcribing') {
            setList(l => (l || []).map(c => (String(c.id) === String(id) ? r.data : c)));
          }
        } catch { /* it will be asked again */ }
      }
    }, 6000);
    return () => clearInterval(timer);
  }, [working, setList]);
}

// The record / upload / paste panel. Exported so the Planner can reuse it to record a meeting.
// `autoStart` is for the one case where the person has already said yes: they pressed Record on
// the meeting prompt, and being shown a second button to press is a delay in the only part of a
// meeting that cannot be caught up on — the beginning of it.
export function NewConversation({ status, busy, onCancel, onSubmit, defaultType = 'phone', autoStart = false }) {
  const [type, setType] = useState(defaultType);
  const [rawMode, setMode] = useState('record');    // record | upload | paste (see `mode` below)
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [secs, setSecs] = useState(0);
  const [clip, setClip] = useState(null);           // { dataUrl, mime, duration, name }
  const [transcript, setTranscript] = useState('');
  const [micErr, setMicErr] = useState('');
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  // Seconds actually recorded, kept in a ref because onstop is bound once at the start and would
  // otherwise close over the initial state. Wall-clock time would count the pauses too.
  const secsRef = useRef(0);

  const canRecord = typeof navigator !== 'undefined' && navigator.mediaDevices && window.MediaRecorder;
  // Every current browser can pause a recording, but not every old one — don't offer a button that
  // would do nothing.
  const canPause = canRecord && typeof window.MediaRecorder.prototype.pause === 'function';


  // The one place the clock runs, so pausing and resuming can't leave two intervals going.
  const tick = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSecs(s => { secsRef.current = s + 1; return s + 1; }), 1000);
  };

  const startRec = async () => {
    setMicErr('');
    try {
      // Mono, with the microphone processing left on: this is a room being transcribed, not a
      // recording being kept.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      // Opus at 32 kbps. Left to itself a browser records at 96 to 128, which is a setting for
      // music, and that is what made a 71-minute meeting arrive as 65 MB and fail before it reached
      // the server at all. Speech being transcribed gains nothing from the rest: an hour at this
      // rate is about 14 MB, and the words come out the same.
      const opts = { audioBitsPerSecond: 32000 };
      for (const t of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4']) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) { opts.mimeType = t; break; }
      }
      let mr;
      try { mr = new MediaRecorder(stream, opts); }
      catch { mr = new MediaRecorder(stream); }     // an older browser that will not be told
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        // Kept as bytes. It used to become a base64 data URL here, which is a third larger and had
        // to be parsed back out of a JSON string at the other end.
        setClip({ blob, mime: blob.type, duration: secsRef.current, name: 'Recording', size: blob.size });
      };
      mr.start();
      recRef.current = mr;
      secsRef.current = 0;
      setRecording(true); setPaused(false); setSecs(0);
      tick();
    } catch (e) { setMicErr('Microphone access was blocked. Allow the mic for this site, or upload a recording instead.'); }
  };
  // Asked for once, by the person who already pressed Record on the meeting prompt. Not a loop and
  // not a retry: if the microphone is refused, the message says so and nothing keeps asking.
  const beganRef = useRef(false);
  useEffect(() => {
    if (!autoStart || beganRef.current || !canRecord) return;
    beganRef.current = true;
    startRec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const stopRec = () => {
    // stop() works from paused as well as recording, so a paused take still saves.
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false); setPaused(false);
  };
  // Step aside mid-conversation without ending the take — the timer stops with it, so the length
  // saved is what was actually recorded.
  const pauseRec = () => {
    const mr = recRef.current;
    if (!mr || mr.state !== 'recording') return;
    mr.pause();
    if (timerRef.current) clearInterval(timerRef.current);
    setPaused(true);
  };
  const resumeRec = () => {
    const mr = recRef.current;
    if (!mr || mr.state !== 'paused') return;
    mr.resume();
    tick();
    setPaused(false);
  };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  // Safety cap: auto-stop an in-app recording at 2 hours so a forgotten one can't run indefinitely.
  const MAX_REC_SECS = 2 * 3600;
  useEffect(() => {
    if (recording && secs >= MAX_REC_SECS) { stopRec(); window.alert('Recording stopped automatically at 2 hours. Save what you have (or record the rest as a second part).'); }
  }, [secs, recording]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setClip({ blob: f, mime: f.type || 'audio/mpeg', duration: null, name: f.name, size: f.size });
  };

  const transcriptionOn = status && status.enabled;
  // With no transcription key, recording/upload can't work — fall back to paste.
  const mode = transcriptionOn ? rawMode : 'paste';
  const canSubmit = mode === 'paste' ? transcript.trim().length > 0 : !!clip;
  const doSubmit = () => {
    if (mode === 'paste') onSubmit({ type, transcript });
    else if (clip) onSubmit({ type, blob: clip.blob, mime_type: clip.mime, duration_sec: clip.duration });
  };

  return (
    <div className="conv-new">
      <div className="conv-types">
        {TYPES.map(t => <button key={t.key} type="button" className={`conv-type ${type === t.key ? 'active' : ''}`} onClick={() => setType(t.key)}>{t.label}</button>)}
      </div>

      <div className="conv-modes">
        {transcriptionOn && <button type="button" className={mode === 'record' ? 'active' : ''} onClick={() => setMode('record')}><Icon name="mic" /> Record</button>}
        {transcriptionOn && <button type="button" className={mode === 'upload' ? 'active' : ''} onClick={() => setMode('upload')}>⬆ Upload audio</button>}
        <button type="button" className={mode === 'paste' ? 'active' : ''} onClick={() => setMode('paste')}>⌨ Paste transcript</button>
      </div>

      <div className="conv-consent">⚠️ Recording a conversation? Let the other person know and get their OK first — for phone calls this is required by law in several Australian states.</div>

      {mode === 'record' && transcriptionOn && (
        <div className="conv-recorder">
          {!canRecord ? <div className="conv-err">This browser can’t record audio — upload a recording or paste a transcript instead.</div>
            : !clip ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {!recording
                  ? <button type="button" className="btn btn-primary" onClick={startRec}>● Start recording</button>
                  : (
                    <>
                      <button type="button" className={`btn btn-danger${paused ? '' : ' conv-rec-live'}`} onClick={stopRec}>■ Stop ({fmtDur(secs)})</button>
                      {canPause && (paused
                        ? <button type="button" className="btn btn-primary btn-sm" onClick={resumeRec}>▶ Resume</button>
                        : <button type="button" className="btn btn-ghost btn-sm" onClick={pauseRec}>❚❚ Pause</button>)}
                    </>
                  )}
                {recording && <span className={`conv-reclabel${paused ? ' paused' : ''}`}>{paused ? 'Paused — nothing being recorded' : 'Recording…'}</span>}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="conv-clip">🎧 Recording ready · {fmtDur(clip.duration)}</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setClip(null)}>Redo</button>
              </div>
            )}
          {micErr && <div className="conv-err" style={{ marginTop: 8 }}>{micErr}</div>}
        </div>
      )}

      {mode === 'upload' && transcriptionOn && (
        <div className="conv-upload">
          <input type="file" accept="audio/*,video/mp4,.m4a,.mp3,.wav,.ogg,.webm" onChange={onFile} />
          {clip && <span className="conv-clip" style={{ marginLeft: 10 }}>🎧 {clip.name} · {(clip.size / 1048576).toFixed(1)} MB</span>}
          <div className="hint hint-xs" style={{ marginTop: 6 }}>Record the call on your phone, then upload the file here. Up to 60 MB — an hour of speech is normally 15 to 30 MB. A file much larger than that was recorded at music quality, and saving it again at a lower one will fix it.</div>
          {clip && clip.size > 60 * 1048576 && (
            <div className="conv-err" style={{ marginTop: 8 }}>
              That file is {(clip.size / 1048576).toFixed(0)} MB, over the 60 MB limit. Save it again at a lower
              quality — speech needs a fraction of what music does.
            </div>
          )}
        </div>
      )}

      {mode === 'paste' && (
        <div style={{ marginTop: 6 }}>
          {type === 'whatsapp' && (
            <div className="hint hint-xs" style={{ marginBottom: 6 }}>
              Paste the chat, or upload a WhatsApp <strong>Export chat</strong> file (⋮ → More → Export chat → Without media):{' '}
              <label className="link-btn" style={{ cursor: 'pointer' }}>
                choose .txt file
                <input type="file" accept=".txt,text/plain" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files && e.target.files[0]; if (!f) return; const fr = new FileReader(); fr.onload = () => setTranscript(String(fr.result || '')); fr.readAsText(f); }} />
              </label>
            </div>
          )}
          <textarea className="form-textarea" style={{ minHeight: 140 }} value={transcript} onChange={e => setTranscript(e.target.value)}
            placeholder={type === 'whatsapp'
              ? 'Paste the WhatsApp thread here (or upload the exported .txt above). AI will summarise it and pull out any follow-ups.'
              : 'Paste or type what was discussed. AI will turn it into minutes and follow-up tasks.\n\nYou can include speaker names, e.g.\nMe: …\nClient: …'} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={doSubmit} disabled={busy || !canSubmit}>
          {busy ? (transcriptionOn && mode !== 'paste' ? 'Sending the recording…' : 'Writing minutes…') : 'Save & summarise'}
        </button>
      </div>
    </div>
  );
}

// Self-contained modal to record/upload/paste a conversation for a client — used by the Planner's
// "Record this meeting" shortcut. Fetches status, posts, then shows the minutes it got back.
export function RecordConversationModal({ clientId, clientName, defaultType = 'in_person', workItemId = null, workTaskId = null, autoStart = false, onClose, onCreated }) {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/transcribe/status').then(r => setStatus(r.data)).catch(() => setStatus({ enabled: false, ai: false })); }, []);
  const submit = async (payload) => {
    setBusy(true); setErr('');
    try {
      const { blob, ...rest } = payload;
      const audio_id = blob ? await uploadAudio(clientId, blob, payload.mime_type, payload.duration_sec) : null;
      const r = await api.post(`/clients/${clientId}/conversations`,
        { ...rest, audio_id, work_item_id: workItemId, work_task_id: workTaskId },
        { timeout: 30 * 60 * 1000 });
      setDone(r.data); if (onCreated) onCreated(r.data);
    }
    catch (e) {
      // A size limit that reports itself as "could not save" is a limit nobody can work around, and
      // that is exactly what happened to a 71-minute recording: the body parser refused it with a
      // response that was not JSON, so there was nothing here to read.
      const d = e.response && e.response.data;
      const status = e.response && e.response.status;
      setErr(
        (d && d.error)
        || (status === 413 ? 'That recording is too large to upload. Record at a lower quality, or split a long call into parts.' : '')
        || (e.code === 'ECONNABORTED' ? 'That took too long and the connection gave up. A very long recording is better split into parts.' : '')
        || (status ? `The server refused it (${status}).` : '')
        || (e.message ? `Could not save that conversation: ${e.message}` : 'Could not save that conversation.'));
    }
    setBusy(false);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title"><Icon name="mic" /> Record — {clientName || 'client'}</div>
        {!done ? (
          <>
            {err && <div className="conv-err">{err}</div>}
            <NewConversation status={status} busy={busy} defaultType={defaultType} autoStart={autoStart} onCancel={onClose} onSubmit={submit} />
          </>
        ) : (
          <div>
            <div className="conv-added" style={{ fontSize: 14 }}>✓ Saved to {clientName}{done.summary && done.summary.title ? ` · “${done.summary.title}”` : ''}</div>
            {done.summary && done.summary.summary && <div className="conv-section"><Markdown text={done.summary.summary} /></div>}
            {done.summary && done.summary.error && <div className="conv-err" style={{ marginTop: 8 }}>Minutes weren’t generated: {done.summary.error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={() => nav(`/clients/${clientId}`)}>Open {clientName || 'client'} → review & add tasks</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationCard({ conv, clientName, onDelete, onChange }) {
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const s = conv.summary || {};
  const items = s.action_items || [];
  const [picked, setPicked] = useState(() => items.map(() => true));
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(conv.work_item_id ? { work_item_id: conv.work_item_id } : null);

  useEffect(() => { setPicked((conv.summary?.action_items || []).map(() => true)); }, [conv.summary]);

  const loadTranscript = async () => {
    if (transcript != null) { setShowTranscript(v => !v); return; }
    try { const r = await api.get(`/conversations/${conv.id}`); setTranscript(r.data.transcript || ''); setShowTranscript(true); }
    catch { setTranscript(''); setShowTranscript(true); }
  };

  // The recording is kept on the server when a write-up fails, precisely so this can exist: an
  // hour of someone's meeting is not something to lose because a transcription service was busy.
  const retry = async () => {
    setAdding(true);
    try { const r = await api.post(`/conversations/${conv.id}/retry`); onChange(r.data); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not start that again.'); }
    setAdding(false);
  };

  const resummarize = async () => {
    setAdding(true);
    try { const r = await api.post(`/conversations/${conv.id}/summarize`, {}); onChange(r.data); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not re-run the summary.'); }
    setAdding(false);
  };

  const addTasks = async () => {
    const chosen = items.filter((_, i) => picked[i]).map(a => ({ title: a.title, description: a.description, priority: a.priority === 'medium' ? 'low' : a.priority, is_client_task: a.owner === 'client' ? 1 : 0, due_hint: a.due_hint }));
    if (!chosen.length) return;
    setAdding(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/tasks`, { items: chosen });
      setAdded({ work_item_id: r.data.work_item_id, created: r.data.created });
    } catch (e) { window.alert(e.response?.data?.error || 'Could not create tasks.'); }
    setAdding(false);
  };

  return (
    <div className="conv-card">
      <div className="conv-card-head" onClick={() => setExpanded(v => !v)}>
        <div style={{ minWidth: 0 }}>
          <div className="conv-title">
            {conv.status === 'transcribing' ? <Icon name="clock" /> : (expanded ? '▾' : '▸')} {s.title || conv.title}
          </div>
          <div className="conv-meta">{typeLabel(conv.type)} · {fmtWhen(conv.occurred_at || conv.created_at)}{conv.duration_sec ? ` · ${fmtDur(conv.duration_sec)}` : ''}{conv.created_by ? ` · ${conv.created_by}` : ''}</div>
          {conv.status === 'transcribing' && (
            <div className="conv-meta" style={{ color: 'var(--primary)' }}>
              Being transcribed and written up. It carries on without you — leave this page if you like, and
              it will be here when it is done.
            </div>
          )}
          {conv.status === 'failed' && (
            <div className="conv-meta" style={{ color: 'var(--danger, #c33)' }}>
              {conv.work_error || 'Something went wrong writing this up.'}
              {' '}The recording is still on the server —{' '}
              <button className="link-btn" onClick={(e) => { e.stopPropagation(); retry(); }} disabled={adding}>try again</button>.
            </div>
          )}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">✕</button>
      </div>

      {expanded && conv.status === 'transcribing' && (
        <div className="conv-body">
          <div className="conv-section hint hint-base">
            Nothing to show yet. An hour of audio takes a few minutes to read and a little longer to write
            up; the transcript appears first and the minutes follow it.
          </div>
        </div>
      )}

      {expanded && conv.status !== 'transcribing' && (
        <div className="conv-body">
          {s.error && <div className="conv-err">Minutes weren’t generated: {s.error} <button className="btn btn-ghost btn-sm" onClick={resummarize} disabled={adding}>Try again</button></div>}
          {s.summary && <div className="conv-section"><Markdown text={s.summary} /></div>}
          {(s.key_points || []).length > 0 && <div className="conv-section"><div className="conv-h">Key points</div><ul>{s.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul></div>}
          {(s.decisions || []).length > 0 && <div className="conv-section"><div className="conv-h">Decisions</div><ul>{s.decisions.map((k, i) => <li key={i}>{k}</li>)}</ul></div>}

          {items.length > 0 && (
            <div className="conv-section">
              <div className="conv-h">Follow-up tasks</div>
              {items.map((a, i) => (
                <label key={i} className="conv-action">
                  <input type="checkbox" checked={!!picked[i]} onChange={e => setPicked(p => p.map((v, j) => j === i ? e.target.checked : v))} disabled={!!added} />
                  <span>
                    <span className={`conv-owner ${a.owner}`}>{a.owner === 'client' ? clientName || 'Client' : 'Us'}</span>
                    <b>{a.title}</b>{a.due_hint ? <span className="conv-due"> · {a.due_hint}</span> : ''}
                    {a.description ? <div className="conv-adesc">{a.description}</div> : null}
                  </span>
                </label>
              ))}
              {added
                ? <div className="conv-added">✓ {added.created ? `${added.created} task${added.created === 1 ? '' : 's'} added` : 'Tasks created'} · <button className="link-btn" onClick={() => nav(`/?focus=${added.work_item_id}`)}>Open the follow-up workflow →</button></div>
                : <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={addTasks} disabled={adding || !picked.some(Boolean)}>{adding ? 'Adding…' : `Add ${picked.filter(Boolean).length} as task${picked.filter(Boolean).length === 1 ? '' : 's'}`}</button>}
            </div>
          )}

          {s.client_note && <div className="conv-section"><div className="conv-h">Note on file</div><div className="conv-note">{s.client_note}</div></div>}

          <div className="conv-section">
            <button className="link-btn" onClick={loadTranscript}>{showTranscript ? 'Hide transcript' : 'Show transcript'}</button>
            {showTranscript && <pre className="conv-transcript">{transcript || '(no transcript)'}</pre>}
          </div>
        </div>
      )}
    </div>
  );
}
